
import React, { useState } from 'react';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { DatabaseBackup, Database } from 'lucide-react';
import { AuthorizedUser } from '@/types/authorizedUser';
import ImportForm, { ImportFormValues } from './ImportForm';
import { mockImportUsers } from './mockImportService';

interface ImportUsersDialogProps {
  onImportSuccess: (users: AuthorizedUser[]) => void;
}

const ImportUsersDialog: React.FC<ImportUsersDialogProps> = ({ onImportSuccess }) => {
  const { toast } = useToast();
  const [isImporting, setIsImporting] = useState(false);
  const [open, setOpen] = useState(false);

  // Form submission handler
  const handleSubmit = async (values: ImportFormValues) => {
    setIsImporting(true);
    
    try {
      // In a real application, this would be an actual API call to connect to the database
      // For this example, we'll simulate a successful import with the mockImportUsers function
      const importedUsers = await mockImportUsers(values);
      
      onImportSuccess(importedUsers);
      
      toast({
        title: "Import Successful",
        description: `Imported ${importedUsers.length} users from external database`,
      });
      
      setOpen(false);
    } catch (error) {
      toast({
        title: "Import Failed",
        description: error instanceof Error ? error.message : "Failed to import users",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="h-10">
          <DatabaseBackup className="mr-2 h-4 w-4" />
          Import from Database
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Database className="mr-2 h-5 w-5" />
            Import Authorized Users
          </DialogTitle>
          <DialogDescription>
            Connect to an external database to import authorized users
          </DialogDescription>
        </DialogHeader>
        
        <ImportForm 
          onSubmit={handleSubmit} 
          onCancel={() => setOpen(false)} 
          isImporting={isImporting} 
        />
      </DialogContent>
    </Dialog>
  );
};

export default ImportUsersDialog;
